function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo } from 'react';
import { useDeviceSpectrum } from '@cbhq/cds-web/hooks/useDeviceSpectrum';
import { Divider } from '@cbhq/cds-web/layout';
import { ThemeProvider } from '@cbhq/cds-web/system/ThemeProvider';
import { NAVBAR_BANNER_HEIGHT, createAnchorId, INTRODUCTION_ID, Div, HeroTertiary, formatDateWithoutTimeZone, NAVBAR_NAV_HEIGHT } from '@cbhq/cdx-components';
import { WayfindingCardArticle } from '../../components/WayfindingCardArticle.js';
import { useContentfulMicrocopy } from '../../globals/microcopy.js';
import { capitalizeRichtextParagraph } from '../../utils/capitalizeRichtextParagraph.js';
import { getEntryProps } from '../../utils/getEntryProps.js';
import { PageTemplateLayout } from '../PageTemplateLayout.js';
import { PageTemplateProviders } from '../PageTemplateProviders.js';
import { EditorialBody } from './EditorialBody.js';
import { EditorialHero } from './EditorialHero.js';
import './RelatedStories/index.js';
import { Sidebar } from './sidebar/Sidebar.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { RelatedStories } from './RelatedStories/RelatedStories.js';
const EDITORIAL_TEMPLATE_CONTENT_MODEL_ID = 'cdxTemplateEditorialPage';
const CDSThemeProviderWithChildren = ThemeProvider;
function EditorialTemplateContent({
  headline,
  subheadRichText,
  tags,
  image,
  imageFit,
  authors,
  readTime,
  articleBody,
  heroType = 'editorial',
  projectSettings,
  editorialWayfinding,
  showSocialSharingLinks = true,
  isEmbeddedView = false,
  siteConfig,
  relatedStories,
  publicationDate,
  path,
  onSearch,
  templateWidth,
  richTextOptions,
  applicationSettingsExtraProps
}) {
  const microcopies = useContentfulMicrocopy();
  const deviceSpectrum = useDeviceSpectrum();
  const hasBanner = !!siteConfig?.fields.bannerText || !!projectSettings?.fields.bannerText;
  const bannerHeight = hasBanner ? NAVBAR_BANNER_HEIGHT : 0;
  const anchors = useMemo(() => {
    if (!articleBody) return;
    capitalizeRichtextParagraph(articleBody?.content);
    let headingAnchors = articleBody.content.filter(heading => heading.nodeType === 'heading-2').map(heading => ({
      text: heading.content[0].value,
      anchor: createAnchorId(heading.content[0].value ?? '')
    }));
    if (articleBody.content[0]?.nodeType === 'paragraph') {
      headingAnchors = [{
        text: microcopies.global?.introduction || 'Introduction',
        anchor: INTRODUCTION_ID
      }, ...headingAnchors];
    }
    return headingAnchors;
  }, [articleBody, microcopies]);
  const _getEntryProps = getEntryProps(projectSettings),
    fallbackImage = _getEntryProps.fallbackImage,
    defaultEditorialWayfinding = _getEntryProps.defaultEditorialWayfinding;
  const fallbackImageUrl = fallbackImage?.fields.file.url;
  const fallbackImageAlt = fallbackImage?.fields.title;
  if (isEmbeddedView) {
    return /*#__PURE__*/jsx(CDSThemeProviderWithChildren, {
      spectrum: deviceSpectrum,
      children: /*#__PURE__*/jsxs(Div, {
        flexShrink: 1,
        flexGrow: 1,
        flexBasis: {
          sm: '100%',
          md: '70%'
        },
        flexDirection: "column",
        children: [/*#__PURE__*/jsx(HeroTertiary, {
          headline: headline || ''
        }), /*#__PURE__*/jsx(EditorialBody, {
          authors: authors,
          tags: tags,
          content: articleBody,
          richTextOptions: richTextOptions,
          isEmbeddedView: isEmbeddedView
        })]
      })
    });
  }
  return /*#__PURE__*/jsxs(PageTemplateLayout, {
    path: path,
    onSearch: onSearch,
    siteConfig: siteConfig,
    templateWidth: templateWidth,
    projectConfig: projectSettings,
    applicationSettingsExtraProps: applicationSettingsExtraProps,
    children: [/*#__PURE__*/jsxs(Div, {
      flexWrap: {
        sm: 'wrap-reverse',
        md: 'nowrap'
      },
      justifyContent: "space-evenly",
      width: "100%",
      height: "fit-content",
      children: [/*#__PURE__*/jsxs(Div, {
        flexShrink: 1,
        flexGrow: 1,
        flexBasis: {
          sm: '100%',
          md: '70%'
        },
        flexDirection: "column",
        children: [/*#__PURE__*/jsx(Div, {
          width: "100%",
          id: INTRODUCTION_ID,
          children: heroType === 'editorial' ? /*#__PURE__*/jsx(EditorialHero, {
            title: headline || '',
            authors: authors,
            tags: tags,
            subheadRichText: subheadRichText,
            image: image,
            imageFit: imageFit,
            readTime: readTime,
            showSocialSharingLinks: showSocialSharingLinks,
            datePublished: formatDateWithoutTimeZone(publicationDate)
          }) : /*#__PURE__*/jsx(HeroTertiary, {
            headline: headline || ''
          })
        }), /*#__PURE__*/jsx(EditorialBody, {
          authors: authors,
          tags: tags,
          content: articleBody,
          richTextOptions: richTextOptions
        })]
      }), /*#__PURE__*/jsxs(Div, {
        flexShrink: 1,
        flexGrow: 1,
        flexBasis: {
          sm: '100%',
          md: '30%'
        },
        flexDirection: "column",
        position: {
          sm: 'fixed',
          md: 'sticky'
        },
        left: 0,
        height: "fit-content",
        top: {
          sm: NAVBAR_NAV_HEIGHT + bannerHeight,
          md: 140
        },
        children: [/*#__PURE__*/jsx(Sidebar, {
          anchors: anchors,
          showSocialSharingLinks: showSocialSharingLinks
        }), relatedStories && /*#__PURE__*/jsx(RelatedStories, {
          articles: relatedStories
        })]
      })]
    }), editorialWayfinding && /*#__PURE__*/jsx(Divider, {
      spacingVertical: 10
    }), (editorialWayfinding || defaultEditorialWayfinding) && /*#__PURE__*/jsx(WayfindingCardArticle, _objectSpread({
      options: {
        fallbackImageUrl,
        fallbackImageAlt
      }
    }, editorialWayfinding ? editorialWayfinding.fields : defaultEditorialWayfinding?.fields))]
  });
}
function EditorialTemplate(props) {
  return /*#__PURE__*/jsx(PageTemplateProviders, {
    siteConfig: props.siteConfig,
    componentMap: props.componentMap,
    cdsPalette: props.cdsPalette,
    cdsThemeMode: props.cdsThemeMode,
    children: /*#__PURE__*/jsx(EditorialTemplateContent, _objectSpread({}, props))
  });
}
export { EDITORIAL_TEMPLATE_CONTENT_MODEL_ID, EditorialTemplate };
//# sourceMappingURL=EditorialTemplate.js.map
